import { computed, onMounted, onUnmounted, ref } from 'vue'
import { fetchItems } from '@/api/replenishments'
import type { IReplenishment } from '@/interfaces/replenishments'

const DAY_MS = 24 * 60 * 60 * 1000
const PAGE_SIZE = 50

export function formatPaymentAmount(
  amount: string | number,
  paymentSystem: string | null | undefined,
  currencySign: string | null | undefined
) {
  const match = (paymentSystem || '').match(/\b(USDT|USDC|BTC|BITCOIN)\b/i)
  const matched = match?.[1]?.toUpperCase()
  const currency = matched === 'BITCOIN' ? 'BTC' : matched

  return currency ? `${amount} ${currency}` : `${currencySign || ''}${amount}`
}

type ActiveRequest = IReplenishment & {
  attributes: IReplenishment['attributes'] & {
    payment_system_sid: string | null
    has_crypto_address: boolean
  }
}

export function useActivePaymentRequests() {
  const requests = ref<ActiveRequest[]>([])
  const now = ref(Date.now())
  const activeRequests = computed(() =>
    requests.value.filter(
      (item) => item.attributes.created_int * 1000 + DAY_MS > now.value
    )
  )
  const hasActiveRequests = computed(() => activeRequests.value.length > 0)
  let generation = 0
  let timer: ReturnType<typeof setInterval> | undefined
  let clock: ReturnType<typeof setInterval> | undefined

  async function refresh() {
    const current = ++generation
    let start = 0
    const found: ActiveRequest[] = []

    try {
      while (current === generation) {
        const response = await fetchItems({
          start,
          limit: PAGE_SIZE,
          filter: { online: false }
        })
        if (current !== generation) return

        const cutoff = Date.now() - DAY_MS
        found.push(
          ...(response.data as ActiveRequest[]).filter(
            (item) =>
              item.attributes.status === 'Created' &&
              item.attributes.created_int * 1000 > cutoff
          )
        )

        start += response.data.length
        if (
          response.data.length === 0 ||
          start >= response.meta.total ||
          response.data.some(
            (item) => item.attributes.created_int * 1000 <= cutoff
          )
        ) {
          requests.value = found
          return
        }
      }
    } catch {
      return
    }
  }

  onMounted(() => {
    refresh()
    timer = setInterval(refresh, 60000)
    clock = setInterval(() => {
      now.value = Date.now()
    }, 1000)
    window.addEventListener('focus', refresh)
  })
  onUnmounted(() => {
    generation++
    clearInterval(timer)
    clearInterval(clock)
    window.removeEventListener('focus', refresh)
  })

  return {
    hasActiveRequests,
    activeRequests,
    now,
    refreshActiveRequests: refresh
  }
}
